import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useChatStore } from './chatStore'
import api from '@/services/api'
import { authService, isMobileBridge } from '@/services/authService'
import type { User } from '@/types/User'
import type { AppSettings } from '@/types/Api'
import { useRoute, type LocationQuery } from 'vue-router'

const DEFAULT_PROBLEM_CATEGORY_CODE = 'Ostatni'

export const useUserStore = defineStore('user', () => {
	const route = useRoute()

	/**
	 * auth state
	 */
	const authIsLoading = ref<boolean>(false)

	const signOut = () => {
		if (isMobileBridge()) {
			alert('mobilní rozhraní se nemůže odhlásit napřímo')
		}

		authService.oidcInstance?.signOut()
	}

	/**
	 * returns facilityCode, workplaceCode and problemCategoryCode
	 */
	const appSettings = ref<AppSettings>({
		facilityCode: '',
		workplaceCode: '',
		problemCategoryCode: ''
	})

	let appSettingsPromise: Promise<void> | null = null

	/**
	 * loads facilityCode, workplaceCode and problemCategoryCode
	 * searches for query params
	 * if not found, calls web url endpoint to retreive them
	 */
	const queryParam = (value: unknown): string | null => {
		const candidate = Array.isArray(value) ? value[0] : value
		return typeof candidate === 'string' && candidate.trim()
			? candidate.trim()
			: null
	}

	const loadAppSettings = (query: LocationQuery = route.query) => {
		if (appSettingsPromise) {
			return appSettingsPromise
		}

		const request = (async () => {
			let facilityCode = queryParam(query['facilityCode'])
			let workplaceCode = queryParam(query['workplaceCode'])
			let problemCategoryCode = queryParam(query['problemCategoryCode'])

			if (!facilityCode || !workplaceCode || !problemCategoryCode) {
				const { data } = await api.get<{ url: string }>(`web/url`)
				const webUrl = new URL(data.url)

				facilityCode = facilityCode || webUrl.searchParams.get('facilityCode')
				workplaceCode =
					workplaceCode || webUrl.searchParams.get('workplaceCode')
				problemCategoryCode =
					problemCategoryCode ||
					webUrl.searchParams.get('problemCategoryCode') ||
					DEFAULT_PROBLEM_CATEGORY_CODE
			}

			if (!facilityCode || !workplaceCode || !problemCategoryCode) {
				throw new Error('Missing required telemedicine application settings')
			}

			appSettings.value = {
				facilityCode,
				workplaceCode,
				problemCategoryCode
			}
		})()

		appSettingsPromise = request
		request.catch(() => {
			if (appSettingsPromise === request) {
				appSettingsPromise = null
			}
		})

		return request
	}

	return {
		authIsLoading,
		appSettings,
		loadAppSettings,
		signOut
	}
})
